import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { useRealtime, useSocket } from "@/hooks/useSocket";
import { AlertsTab } from "./tabs/AlertsTab";
import { CommandsTab } from "./tabs/CommandsTab";
import { HitsTab } from "./tabs/HitsTab";
import { PlaybackTab } from "./tabs/PlaybackTab";
import { ReportsTab } from "./tabs/ReportsTab";
import { SessionsTab } from "./tabs/SessionsTab";
import { SummaryTab } from "./tabs/SummaryTab";

type TabId =
  | "summary"
  | "sessions"
  | "alerts"
  | "hits"
  | "playback"
  | "commands"
  | "reports";

const TABS: { id: TabId; label: string }[] = [
  { id: "summary", label: "Resumen" },
  { id: "sessions", label: "Sesiones" },
  { id: "alerts", label: "Alertas" },
  { id: "hits", label: "Hits" },
  { id: "playback", label: "Playback" },
  { id: "commands", label: "Comandos" },
  { id: "reports", label: "Reportes" },
];

export function UnitDetailPage() {
  const { id } = useParams<{ id: string }>();
  const unitId = Number(id);
  const qc = useQueryClient();
  const socket = useSocket().socket;
  const [tab, setTab] = useState<TabId>("summary");

  // Subscribe to per-unit room while the page is mounted.
  useEffect(() => {
    if (!Number.isFinite(unitId) || !socket) return;
    socket.emit("unit.join", { unitId });
    return () => {
      socket.emit("unit.leave", { unitId });
    };
  }, [unitId, socket]);

  // Realtime invalidations scoped to this unit.
  useRealtime("unit.track", () => {
    qc.invalidateQueries({ queryKey: ["unit-detail", unitId] });
  });
  useRealtime("unit.session.stopped", () => {
    qc.invalidateQueries({ queryKey: ["unit-detail", unitId] });
    qc.invalidateQueries({ queryKey: ["unit-sessions", unitId] });
  });
  useRealtime("unit.alert", () => {
    qc.invalidateQueries({ queryKey: ["unit-alerts", unitId] });
    qc.invalidateQueries({ queryKey: ["unit-detail", unitId] });
  });
  useRealtime("unit.kpi.refreshed", () => {
    qc.invalidateQueries({ queryKey: ["unit-detail", unitId] });
  });

  const renderTab = useMemo(() => {
    if (!Number.isFinite(unitId)) return null;
    switch (tab) {
      case "summary":
        return <SummaryTab unitId={unitId} />;
      case "sessions":
        return <SessionsTab unitId={unitId} />;
      case "alerts":
        return <AlertsTab unitId={unitId} />;
      case "hits":
        return <HitsTab unitId={unitId} />;
      case "playback":
        return <PlaybackTab unitId={unitId} />;
      case "commands":
        return <CommandsTab unitId={unitId} />;
      case "reports":
        return <ReportsTab unitId={unitId} />;
    }
  }, [tab, unitId]);

  if (!Number.isFinite(unitId)) {
    return <div className="p-6 text-muted-200">Unidad inválida.</div>;
  }

  return (
    <div
      className="p-4 space-y-4 max-w-6xl mx-auto"
      data-testid={`page-unit-${unitId}`}
    >
      <nav className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={
              "px-3 py-1.5 text-sm rounded-t-md border-b-2 transition " +
              (tab === t.id
                ? "border-primary-400 text-primary-300"
                : "border-transparent text-muted-200 hover:text-foreground hover:bg-surface2")
            }
            data-testid={`unit-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="min-h-[400px]">{renderTab}</div>
    </div>
  );
}
